import React from "react";
import PropTypes from "prop-types";

// Descriptions must match what the row-level security policies actually enforce: "network" admits
// any signed-in Qwoted user (no connection graph is checked).
//
// V1-COMPAT: "hidden" is intentionally omitted from the offered options in both apps while
// V1 is still serving traffic (unconditional — not gated on V1_HOSTED); remove this suppression
// once V1 retires. A record that already has "hidden" stays valid data; only the UI option to
// pick it goes away.
const OPTIONS = [
  { value: "public", label: "Public", description: "Visible to everyone, including search engines." },
  { value: "network", label: "Network", description: "Visible to signed-in Qwoted users only." },
];

/**
 * VisibilityControl — the shared public/network control for profile edit modals (PR and
 * Expert/Hybrid this round; media keeps its existing binary checkbox). Presentational: the
 * caller owns the value and the write path (which differs by engine — PR writes the profile's
 * own `visibility`, Expert/Hybrid write the entity's).
 *
 * `onChange` fires only from an actual click on one of the radio options — never on mount
 * and never as a side effect of the current `value` — so a caller can tell "untouched" apart
 * from "explicitly set." That distinction is what protects a profile sitting at "network":
 * leaving the control alone must never widen (or narrow) its audience on save.
 *
 * @param {Object} props
 * @param {string} [props.value] - The current visibility ("public" | "network" | "hidden" — "hidden"
 *   is valid on existing records but is not offered as a selectable option; see the V1-COMPAT note
 *   on OPTIONS above)
 * @param {Function} props.onChange - Called with the newly chosen value on user interaction
 * @param {string} [props.name] - Radio group name (default "visibility"; override when more
 *   than one instance renders on the same page)
 */
function VisibilityControl({ value = null, onChange, name = "visibility" }) {
  return (
    <div className="visibility-control" role="radiogroup" aria-label="Profile Visibility">
      {OPTIONS.map((option) => (
        <div className="form-check mb-2" key={option.value}>
          <input
            type="radio"
            id={`${name}-${option.value}`}
            name={name}
            className="form-check-input"
            checked={value === option.value}
            onChange={() => onChange(option.value)}
          />
          <label htmlFor={`${name}-${option.value}`} className="form-check-label">
            <span className="fw-semibold d-block">{option.label}</span>
            <span className="d-block text-neutral-60 paragraph-sm">{option.description}</span>
          </label>
        </div>
      ))}
    </div>
  );
}

VisibilityControl.propTypes = {
  value: PropTypes.oneOf(["public", "network", "hidden"]),
  onChange: PropTypes.func.isRequired,
  name: PropTypes.string,
};

export default VisibilityControl;
